/**
 * Thin wrappers around orval-generated Evals V2 viewer hooks.
 *
 * The eval viewer is read-only — runs are pushed by external pipelines
 * via the public API. The mutations are UI-owned toggles: the per-group
 * auto-sync switch and the TRA-947 renames (label only; renaming locks
 * the name against ingest pushes). The generated hooks return
 * `{data, status, headers}` per orval v8 FETCH client; these wrappers
 * unwrap to a flat shape.
 */

import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'

import { type ApiRequestError, getApiErrorDetail } from '@/api/client'
import type { getOverviewApiEvalGroupsOverviewGetResponse } from '@/api/generated/evals/evals'
import {
  getGetGroupApiEvalGroupsSlugGetQueryKey,
  getGetOverviewApiEvalGroupsOverviewGetQueryKey,
  useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet,
  useGetGroupApiEvalGroupsSlugGet,
  useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet,
  useGetGroupsForModelApiEvalGroupsForModelModelIdGet,
  useGetOverviewApiEvalGroupsOverviewGet,
  useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet,
  useGetTaskApiEvalGroupsSlugTasksTaskSlugGet,
  useRenameGroupApiEvalGroupsSlugPatch,
  useRenameTaskApiEvalGroupsSlugTasksTaskSlugPatch,
  useSetGroupSyncApiEvalGroupsSlugSyncPatch,
} from '@/api/generated/evals/evals'
import type {
  EvalGroupAnalysisResponse,
  EvalGroupSummary,
  EvalGroupDetailResponse,
  EvalRunDetailResponse,
  EvalsOverviewResponse,
  RolloutTrajectory,
  EvalTaskDetailResponse,
} from '@/api/generated/model'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useEvalsOverview(): UnwrappedQuery<EvalsOverviewResponse> {
  const query = useGetOverviewApiEvalGroupsOverviewGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useEvalGroup(slug: string | undefined): UnwrappedQuery<EvalGroupDetailResponse> {
  const query = useGetGroupApiEvalGroupsSlugGet(slug ?? '', {
    query: { enabled: !!slug },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useEvalGroupAnalysis(
  slug: string | undefined
): UnwrappedQuery<EvalGroupAnalysisResponse> {
  const query = useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet(slug ?? '', {
    query: { enabled: !!slug },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useEvalTask(
  groupSlug: string | undefined,
  taskSlug: string | undefined
): UnwrappedQuery<EvalTaskDetailResponse> {
  const query = useGetTaskApiEvalGroupsSlugTasksTaskSlugGet(groupSlug ?? '', taskSlug ?? '', {
    query: { enabled: !!groupSlug && !!taskSlug },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** The overview envelope as cached by the orval FETCH client. */
type OverviewQueryData = getOverviewApiEvalGroupsOverviewGetResponse | undefined

/**
 * Rename an eval group or task (TRA-947), applied optimistically.
 *
 * A rename is a label-only change the server can't reject on content (the
 * slug is the key and never moves), so the overview cache — the /evals
 * cards and their task chips — updates immediately and the dialog is
 * already closed. Rolled back and toasted if the server disagrees. Detail
 * pages ride the onSettled invalidation instead of a cache write: their
 * payloads nest the name in several shapes and a refetch is cheap there.
 */
interface RenameSnapshotHandlers {
  onMutate: (name: string) => Promise<{ overview: OverviewQueryData; key: QueryKey }>
  onError: (
    error: ApiRequestError,
    snapshot: { overview: OverviewQueryData; key: QueryKey } | undefined
  ) => void
  onSettled: () => void
}

function useRenameEval(
  patchOverview: (overview: EvalsOverviewResponse, name: string) => EvalsOverviewResponse
): RenameSnapshotHandlers {
  const queryClient = useQueryClient()
  const key = getGetOverviewApiEvalGroupsOverviewGetQueryKey()
  return {
    onMutate: async (name) => {
      // An in-flight overview refetch would land after this write and
      // undo it — cancel first.
      await queryClient.cancelQueries({ queryKey: key })
      const snapshot: OverviewQueryData = queryClient.getQueryData(key)
      queryClient.setQueryData(key, (current: OverviewQueryData): OverviewQueryData => {
        if (current?.status !== 200) return current
        return { ...current, data: patchOverview(current.data, name) }
      })
      return { overview: snapshot, key }
    },
    onError: (error, snapshot) => {
      if (snapshot !== undefined) {
        queryClient.setQueryData(snapshot.key, snapshot.overview)
      }
      toast.error(getApiErrorDetail(error) ?? 'Rename failed. Try again')
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  }
}

/**
 * Every cached query that carries the renamed labels. Orval keys are the
 * request URL, so one prefix match covers the group detail, analysis, task
 * detail AND run pages (runs embed group_display_name/task_display_name) —
 * enumerating keys one by one kept missing carriers. The for-model listing
 * embeds a model id this hook can't know, so it gets its own prefix.
 */
function invalidateNameCarriers(
  queryClient: ReturnType<typeof useQueryClient>,
  groupSlug: string
): void {
  const groupPrefix = `/api/eval-groups/${groupSlug}`
  void queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === 'string' &&
      (query.queryKey[0].startsWith(groupPrefix) ||
        query.queryKey[0].startsWith('/api/eval-groups/for-model/')),
  })
}

/** Rename one eval group; the /evals card updates on the spot. */
export function useRenameEvalGroup(groupSlug: string): {
  rename: (name: string) => void
} {
  const queryClient = useQueryClient()
  const snapshotHandlers = useRenameEval((overview, name) => ({
    ...overview,
    groups: overview.groups.map((group) =>
      group.slug === groupSlug ? { ...group, display_name: name } : group
    ),
  }))
  const mutation = useRenameGroupApiEvalGroupsSlugPatch<
    ApiRequestError,
    Awaited<ReturnType<typeof snapshotHandlers.onMutate>>
  >({
    mutation: {
      onMutate: (variables) => snapshotHandlers.onMutate(variables.data.display_name),
      onError: (error, _variables, snapshot) => { snapshotHandlers.onError(error, snapshot) },
      onSettled: () => {
        snapshotHandlers.onSettled()
        invalidateNameCarriers(queryClient, groupSlug)
      },
    },
  })
  return {
    rename: (name) => {
      mutation.mutate({ slug: groupSlug, data: { display_name: name } })
    },
  }
}

/** Rename one task; its chip on the /evals card updates on the spot. */
export function useRenameEvalTask(groupSlug: string, taskSlug: string): {
  rename: (name: string) => void
} {
  const queryClient = useQueryClient()
  const snapshotHandlers = useRenameEval((overview, name) => ({
    ...overview,
    groups: overview.groups.map((group) =>
      group.slug === groupSlug
        ? {
            ...group,
            tasks: group.tasks.map((task) =>
              task.slug === taskSlug ? { ...task, display_name: name } : task
            ),
          }
        : group
    ),
  }))
  const mutation = useRenameTaskApiEvalGroupsSlugTasksTaskSlugPatch<
    ApiRequestError,
    Awaited<ReturnType<typeof snapshotHandlers.onMutate>>
  >({
    mutation: {
      onMutate: (variables) => snapshotHandlers.onMutate(variables.data.display_name),
      onError: (error, _variables, snapshot) => { snapshotHandlers.onError(error, snapshot) },
      onSettled: () => {
        snapshotHandlers.onSettled()
        invalidateNameCarriers(queryClient, groupSlug)
      },
    },
  })
  return {
    rename: (name) => {
      mutation.mutate({ slug: groupSlug, taskSlug, data: { display_name: name } })
    },
  }
}

/** The group page's auto-sync toggle; refetches the group on success. */
export function useSetGroupSync(slug: string): {
  setEnabled: (enabled: boolean) => void
  isPending: boolean
} {
  const queryClient = useQueryClient()
  const mutation = useSetGroupSyncApiEvalGroupsSlugSyncPatch({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetGroupApiEvalGroupsSlugGetQueryKey(slug),
        })
      },
    },
  })
  return {
    setEnabled: (enabled: boolean) => {
      mutation.mutate({ slug, data: { enabled } })
    },
    isPending: mutation.isPending,
  }
}

/**
 * The full trajectory behind one eval-run case (eval-rollouts bucket
 * prefix). Fetched on demand when a case row is expanded — a 404 means no
 * rollout was exported for the trace, which the row states.
 */
export function useEvalCaseRollout(traceId: string): UnwrappedQuery<RolloutTrajectory> {
  const query = useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet(traceId)
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useEvalRun(
  groupSlug: string | undefined,
  taskSlug: string | undefined,
  runId: string | undefined
): UnwrappedQuery<EvalRunDetailResponse> {
  const query = useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet(
    groupSlug ?? '',
    taskSlug ?? '',
    runId ?? '',
    { query: { enabled: !!groupSlug && !!taskSlug && !!runId } }
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * This model's best eval-group scores, highest first.
 *
 * Scoped by the model's declared eval name (`eval_model_slug`, defaulting
 * to its slug) — eval runs carry a free-text model snapshot with no FK, so
 * a team can serve `wilson-v1` and score it as `cartage-1`.
 */
export function useEvalsForModel(
  modelId: string | undefined,
): UnwrappedQuery<EvalGroupSummary[]> {
  const query = useGetGroupsForModelApiEvalGroupsForModelModelIdGet(
    modelId ?? '',
    undefined,
    { query: { enabled: !!modelId } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
